/**
 * Where the copy of an absolutely positioned component (report designer, absolute design) goes.
 *
 * The copy steps one component-size down and right of the original, as it always has. Two cases used to
 * lose it: a component wider than the room to its right put the copy past the page edge (it was in the
 * item tree but not on the page), and duplicating the same component again — or several stacked ones at
 * once — put a copy exactly on top of an existing one. So the copy is kept inside the page, and while a
 * sibling already sits at the target it steps further.
 */
export interface IDuplicateBox {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface IDuplicateOffset {
  dx: number;
  dy: number;
}

export interface IOccupiedOrigin {
  left: number;
  top: number;
}

/** How far a copy steps again when its target is already taken. */
export const DUPLICATE_CASCADE_STEP: number = 10;
const MAX_CASCADE_STEPS: number = 100;

const clampLeft = (left: number, width: number, pageWidth: number | null): number =>
  pageWidth !== null && pageWidth > 0 && left + width > pageWidth ? Math.max(0, pageWidth - width) : left;

export function resolveDuplicateOffset(
  original: IDuplicateBox,
  siblings: readonly IOccupiedOrigin[],
  pageWidth: number | null
): IDuplicateOffset {
  const isTaken = (left: number, top: number): boolean =>
    siblings.some((s: IOccupiedOrigin): boolean => s.left === left && s.top === top);

  let left: number = clampLeft(original.left + original.width, original.width, pageWidth);
  let top: number = original.top + original.height;
  for (let step: number = 0; step < MAX_CASCADE_STEPS && isTaken(left, top); step += 1) {
    left = clampLeft(left + DUPLICATE_CASCADE_STEP, original.width, pageWidth);
    top += DUPLICATE_CASCADE_STEP;
  }
  return { dx: left - original.left, dy: top - original.top };
}
